//src/app/features/taxEstimates/ViewTaxEstimate.tsx
import { yupResolver } from '@hookform/resolvers/yup';
import {
  DashboardContainerHeader,
  ScrollContainer,
} from 'app/layout/dashboardLayout/BoxLayout';
import {
  useUpdateTaxEstimateMutation,
  useUpdateTaxEstimateStatusMutation,
} from 'app/services/taxEstimates';
import { EditIcon, SaveIcon } from 'assets/icons';
import Button from 'Components/Buttons/Button';
import { errorToast, successToast } from 'Components/Notifications';
import OverlayWindow, { WindowSize } from 'Components/OverlayWindow';
import { useCreatePopup } from 'Components/Popups';
import SelectDropDown from 'Components/SelectDropDown';
import Spinner from 'Components/Spinner';
import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'utils/AuthProvider';

import {
  getPreviewFields,
  KeyValueGrid,
} from '../questionnaires/components/PreviewTable';
import { TaxEstimateFields } from './TaxEstimateForm/FormFields';

import { TextInput } from 'Components/Auth/TextInput';
import Form from 'Components/Form';
import * as Yup from 'yup';
import useTaxEstimate from './useTaxEstimate';
const validationSchema = Yup.object({
  estimated_fees_cents: Yup.number()
    .nullable()
    .required('required')
    .typeError('required')
    .max(9999999, 'Invalid price')
    .min(1, 'Invalid price'),
});
export default function ViewTaxEstimate() {
  const { data } = useTaxEstimate();
  const navigate = useNavigate();

  const Popup = useCreatePopup();
  const inputRef = useRef<HTMLInputElement>(null);

  const { isClient } = useAuth();
  const [UpdateStatusTrigger] = useUpdateTaxEstimateStatusMutation();

  const [UpdateTrigger, { isLoading }] = useUpdateTaxEstimateMutation();
  if (!data) return <Spinner />;
  const form = data.data;
  const [first, ...sections] = getPreviewFields(TaxEstimateFields, form);

  return (
    <>
      <Popup.Component>
        <OverlayWindow size={WindowSize.xs}>
          <Form
            defaultValues={{
              estimated_fees_cents: data.estimated_fees_cents,
            }}
            validationSchema={validationSchema}
            className="flex gap-4 w-full flex-1"
            onSubmit={({ estimated_fees_cents }) => {
              UpdateTrigger({
                id: data.id,
                estimated_fees_cents,
              })
                .unwrap()
                .then(() => successToast('Price updated'))
                .then(Popup.close)
                .catch(() => errorToast('Error'));
            }}
          >
            {() => (
              <>
                <TextInput
                  autoFocus
                  type="number"
                  name={'estimated_fees_cents'}
                />
                <Button type="submit" isLoading={isLoading} className="">
                  <SaveIcon className="w-4 h-4" />
                </Button>
              </>
            )}
          </Form>
        </OverlayWindow>
      </Popup.Component>
      <DashboardContainerHeader title={data.title}>
        {isClient && data.status === 'submitted' && (
          <Button className="max-w-min" onClick={() => navigate(`edit`)}>
            <EditIcon className="w-4 h-4" />
            Edit
          </Button>
        )}

        <div className="flex gap-2">
          {data.estimated_fees_cents && (
            <span className="text-green font-semibold flex items-center">
              ${data.estimated_fees_cents}
            </span>
          )}

          {!isClient && data.status === 'in_progress' && (
            <button
              className="form__input flex items-center justify-center"
              onClick={Popup.toggle}
              ref={Popup.setAnchor}
            >
              Set Estimated Tax
            </button>
          )}

          <div className="w-[250px]">
            <SelectDropDown
              clearable={false}
              placeholder={_.capitalize(data.status?.replace('_', ' '))}
              disabled={
                isClient ||
                data.status === 'completed' ||
                data.status === 'cancelled'
              }
              value={data.status}
              options={
                data.status === 'submitted'
                  ? [
                      { id: 'submitted', label: 'Submitted' },
                      { id: 'in_progress', label: 'In Progress' },
                      { id: 'cancelled', label: 'Cancelled' },
                    ]
                  : data.status === 'in_progress'
                  ? [
                      { id: 'in_progress', label: 'In Progress' },
                      { id: 'completed', label: 'Completed' },
                      { id: 'cancelled', label: 'Cancelled' },
                    ]
                  : data.status === 'completed'
                  ? [{ id: 'completed', label: 'Completed' }]
                  : [{ id: 'cancelled', label: 'Cancelled' }]
              }
              onChange={(status) => {
                if (status === 'completed' && !data.estimated_fees_cents) {
                  errorToast(' Estimate Price is required');
                }

                return (
                  status &&
                  UpdateStatusTrigger({ id: data.id, status })
                    .unwrap()
                    .then(() => successToast('Tax Estimate Status Updated'))
                );
              }}
            />
          </div>
        </div>
      </DashboardContainerHeader>

      <ScrollContainer>
        <div className="grid w-full gap-4 pb-4 border-gray-400 shadow">
          {sections.map(
            (section) =>
              section.dependencies &&
              form &&
              getPreviewFields(section.dependencies, form).some(
                // @ts-ignore
                (f) => !!form[f.name],
              ) && (
                <KeyValueGrid
                  headerClassName="bg-gray-400"
                  sectionTitle={{
                    render: (
                      <div
                        className="text-gray-dark text-regular font-semibold"
                        children={section.dependenciesTitle}
                      />
                    ),
                    width: '1fr',
                  }}
                  data={getPreviewFields(section.dependencies, form)}
                  value={form}
                />
              ),
          )}
        </div>
      </ScrollContainer>
    </>
  );
}
